import { FormattedDate, FormattedNumber, useIntl } from 'react-intl';
import { DataTable, useMediaQuery } from '@openedx/paragon';
import { OrderHistoryResponse } from 'shared/types/cart';
import { PageLoader } from 'components/ui/atoms/loader';
import { ErrorAlert, OrderItem, ViewOrderDetailsHyperlink } from './components';
import { messages } from './messages';

const MOBILE_PAYMENT_PROCESSOR_TO_MESSAGE_MAP = new Map([
  [
    'android_iap_edx',
    'payment.order.history.page.payment.processor.heading.android',
  ],
  ['ios_iap_edx', 'payment.order.history.page.payment.processor.heading.ios'],
]);

export const OrderHistory = ({
  isError,
  isLoading,
  orders,
}: OrderHistoryResponse) => {
  const { formatMessage } = useIntl();

  const isMobileView = useMediaQuery({ query: '(max-width: 768px)' });
  const transformedData =
    isError || isLoading
      ? []
      : orders.results.map((result) => {
          const isFreeOrder =
            result.total_excl_tax === '0.0' &&
            result.payment_method === 'Unknown';

          const isWebsiteOrder = !result.mobile_order;
          const paymentProcessorKey = result.payment_processor;

          return {
            items: <OrderItem lineItems={result.lines} />,
            date_placed: <FormattedDate value={result.date_placed} />,
            total_cost: (
              <FormattedNumber
                value={+result.total_excl_tax}
                // eslint-disable-next-line react/style-prop-object
                style="currency"
                currency={result.currency}
              />
            ),
            order_number: result.number,
            order_details:
              !isFreeOrder && isWebsiteOrder ? (
                <ViewOrderDetailsHyperlink orderNumber={result.number} />
              ) : !isWebsiteOrder &&
                MOBILE_PAYMENT_PROCESSOR_TO_MESSAGE_MAP.has(
                  paymentProcessorKey,
                ) ? (
                formatMessage(
                  messages[
                    MOBILE_PAYMENT_PROCESSOR_TO_MESSAGE_MAP.get(
                      paymentProcessorKey,
                    ) as keyof typeof messages
                  ],
                )
              ) : null,
          };
        });

  const hasNoOrders = transformedData.length === 0;

  const renderOrdersTable = () => (
    <DataTable
      isSelectable={false}
      itemCount={transformedData.length}
      data={transformedData}
      columns={[
        {
          Header: formatMessage(
            messages['payment.order.history.table.column.items'],
          ),
          accessor: 'items',
        },
        {
          Header: formatMessage(
            messages['payment.order.history.table.column.date.placed'],
          ),
          accessor: 'date_placed',
        },
        {
          Header: formatMessage(
            messages['payment.order.history.table.column.total.cost'],
          ),
          accessor: 'total_cost',
        },
        {
          Header: formatMessage(
            messages['payment.order.history.table.column.order.number'],
          ),
          accessor: 'order_number',
        },
        {
          Header: formatMessage(
            messages['payment.order.history.table.column.order.details'],
          ),
          accessor: 'order_details',
        },
      ]}
    >
      <DataTable.Table />
    </DataTable>
  );

  const renderMobileOrdersTable = () =>
    transformedData.map((item) => (
      <div className="border-bottom py-3" key={item.order_number}>
        <dl>
          <dt>
            {formatMessage(
              messages['payment.order.history.table.column.items'],
            )}
          </dt>
          <dd>{item.items}</dd>
          <dt>
            {formatMessage(
              messages['payment.order.history.table.column.date.placed'],
            )}
          </dt>
          <dd>{item.date_placed}</dd>
          <dt>
            {formatMessage(
              messages['payment.order.history.table.column.total.cost'],
            )}
          </dt>
          <dd>{item.total_cost}</dd>
          <dt>
            {formatMessage(
              messages['payment.order.history.table.column.order.number'],
            )}
          </dt>
          <dd>{item.order_number}</dd>
        </dl>
        <p>{item.order_details}</p>
      </div>
    ));

  if (isLoading) {
    return (
      <PageLoader
        loaderText={formatMessage(
          messages['payment.order.history.orders.loader'],
        )}
      />
    );
  }

  return (
    <div className="container-fluid py-5">
      <ErrorAlert isVisible={isError} />
      <h1 className="mb-4">
        {formatMessage(messages['payment.order.history.page.heading'])}
      </h1>
      {hasNoOrders ? (
        <p>{formatMessage(messages['payment.order.history.no.orders'])}</p>
      ) : isMobileView ? (
        renderMobileOrdersTable()
      ) : (
        renderOrdersTable()
      )}
    </div>
  );
};
